<template>
  <validation-provider
    #default="{ errors }"
    :name="name"
    :rules="rules"
  >
    <b-form-group
      :id="computedInputId"
      :label="label"
      :label-for="computedInputId"
      :class="containerClass"

      label-class="text-[14px] mb-[8px] text-[#626262]"
    >
      <slot
        name="input"
        :data-id="computedInputId"
        :value="displayValue"
        :placeholder="placeholder"
        :required="required"
        :step="step"
        :disabled="disabled"
        :type="type"
        :onInput="onInput"
        :onBlur="onBlur"
        :onFocus="onFocus"
      >
        <b-form-input
          :id="computedInputId"
          :value="displayValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :step="step"
          :type="type"
          class="!text-[#333333] border disabled:!bg-[#F4F4F4] !py-[10px]"
          style="border-color: #E2E2E2!important;"
          @input="onInput($event)"
          @blur="onBlur($event)"
          @focus="onFocus($event)"
        />
      </slot>
      <slot
        name="error"
        :errors="errors"
      >
        <small
          v-if="errors[0]"
          class="text-danger"
        >{{ errors[0] }}</small>
      </slot>
    </b-form-group>
  </validation-provider>
</template>

<script>
import { IDR } from '@/libs/currency'
import { ValidationProvider } from 'vee-validate'

/**
 * ReusableInput
 * @description ReusableInput is a component that displays the input of the warehouse, specifically for the warehouse information
 * @param {String} value - The value
 * @param {String} name - The name
 * @param {String} label - The label
 * @param {String} placeholder - The placeholder
 * @param {String} rules - The rules
 * @param {Boolean} required - The required
 * @param {Boolean} disabled - The disabled
 * @param {String} type - The type
 * @param {String} inputId - The input id
 * @param {Boolean} noLabel - The no label
 * @param {Boolean} currency - The currency
 * @param {String} containerClass - The container class
 * @returns {Object} ReusableInput
 */
export default {
  name: 'ReusableInput',
  components: {
    ValidationProvider,
  },
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    step: {
      type: [String, Number],
      default: null,
    },
    type: {
      type: String,
      default: 'text',
    },
    inputId: {
      type: String,
      default: '',
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: Boolean,
      default: false,
    },
    containerClass: {
      type: String,
      default: '!mb-[16px]',
    },
  },
  computed: {
    computedInputId() {
      return this.inputId || `input-${this.name.replace(/\s+/g, '-').toLowerCase()}`
    },
    displayValue() {
      if (this.currency && this.value) {
        return IDR(this.value)
      }
      return this.value
    },
  },
  methods: {
    /**
     * Parse the currency
     * @param {String} value - The value
     * @returns {String} The parsed value
     */
    parseCurrency(value) {
      return String(value).replace(/[^\d]/g, '')
    },
    /**
     * @description Handle input event.
     * If currency is true, the value would be parsed back from IDR format to numeric value.
     */
    onInput(event) {
      if (this.currency) {
        const numericValue = this.parseCurrency(event)
        this.$emit('input', numericValue)
      } else {
        this.$emit('input', event)
      }
    },
    /**
     * Handle the blur event
     * @param {Event} event - The event
     */
    onBlur(event) {
      this.$emit('blur', event)
    },
    /**
     * Handle the focus event
     * @param {Event} event - The event
     */
    onFocus(event) {
      this.$emit('focus', event)
    },
  },
}
</script>
